import React, { useState, useCallback, useEffect, useRef } from 'react';
import block from 'bem-cn';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Select from 'components/Select/desktop';
import Button from 'components/Button';
import InputDate from 'components/InputDate/desktop';
import SearchUserInput from 'components/SearchUserInput/SearchUserInput';

import DaysFilter from 'features/reports/view/desktop/DaysFilter';

import { providerItems, gameTypes, statusBets } from 'features/reports/data';

import { actions } from 'features/reports/redux';
import { actions as usersActions } from 'features/users/redux';

import './FilterBlock.scss';

const b = block('filter-block-player-history');

const FilterBlock = ({ count, currentPage, onPageClick }) => {
  const locale = useSelector(state => state.locale.locale);
  const dispatch = useDispatch();
  const locationRouter = useLocation();
  const allowedTime = 2678400000; // 31 дней

  const userFromURL = new URLSearchParams(locationRouter.search).get('username') ?? '';

  const [date, setDate] = useState({
    beginDate: new Date(dayjs().add(-1, 'month')),
    endDate: new Date(dayjs()),
  });
  const [activeDay, setActiveDay] = useState('');

  const [filters, setFilters] = useState({
    user: userFromURL,
    provider: providerItems[0],
    gameType: gameTypes[0],
    statusBet: statusBets[0],
  });

  const [filterValue, setFilterValue] = useState(userFromURL);
  const [searchResult, setSearchResult] = useState([]);
  const [isLoadingSearchResult, setIsLoadingSearchResult] = useState(false);

  const debouncedSearchUser = useRef(
    debounce(value => {
      if (value.length >= 4) {
        setIsLoadingSearchResult(true);
        dispatch(
          usersActions.getUsersListForSearch({
            username: value,
            callback: data => {
              if (data) {
                setSearchResult(data.map(item => item.username));
              }
              setIsLoadingSearchResult(false);
            },
          }),
        );
      }
    }, 800),
  ).current;

  const handleSearchChange = e => {
    const { value } = e.target;
    setFilterValue(value);

    if (value.length >= 4) {
      setIsLoadingSearchResult(true);
      debouncedSearchUser(value);
    } else {
      setIsLoadingSearchResult(false);
      setSearchResult([]);
    }
  };

  const handleResultClick = username => {
    setFilterValue(username);
    setIsLoadingSearchResult(false);
    setSearchResult([]);
    setFilters({ ...filters, user: username });
  };

  const onChangeBeginDate = value => {
    if (!isNaN(value.valueOf())) {
      const newEndDate = new Date(dayjs(value).add(1, 'month'));
      setDate({ beginDate: value, endDate: newEndDate });
    }
    setActiveDay('');
  };

  const onChangeEndDate = value => {
    if (!isNaN(value.valueOf())) {
      const timeDiff = new Date(value).getTime() - new Date(date.beginDate).getTime();
      if (timeDiff > allowedTime) {
        setDate({ ...date, endDate: new Date(dayjs(date.beginDate).add(1, 'month').add(1, 'day')) });
      } else if (value <= date.beginDate) {
        setDate({ ...date, endDate: new Date(dayjs(date.beginDate).add(1, 'day')) });
      } else {
        setDate({ ...date, endDate: value });
      }
    }
  };

  const handleProviderChange = value => {
    const providerItem = providerItems.find(item => item.value === value);
    setFilters({ ...filters, provider: providerItem });
  };

  const handleStatusBetChange = value => {
    const statusBet = statusBets.find(item => item.value === value);
    setFilters({ ...filters, statusBet });
  };

  const firstUpdate = useRef(!userFromURL);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    handleSearch();
  }, [count, currentPage]);

  const onClickSearch = () => {
    handleSearch();
    onPageClick(0);
    setSearchResult([]);
  };

  const handleSearch = useCallback(() => {
    const username = filterValue.trim();
    if (!username) return;
    const requestedData = {
      username,
      count,
      provider: filters.provider.value,
      status: filters.statusBet.value,
      gameType: filters.gameType.value,
      page: currentPage,
      ...date,
    };
    dispatch(actions.getSlotsHistory(requestedData));
  }, [filterValue, filters, date, count, currentPage, dispatch]);

  const onChangeActiveDay = useCallback(
    dayFilter => {
      setActiveDay(dayFilter);
      setDate({ ...date, beginDate: dayFilter.value, endDate: dayFilter.value2 });
    },
    [date],
  );

  const handleFormSubmit = e => {
    e.preventDefault();
    onClickSearch();
  };

  const handleKeyDown = e => {
    if (e?.key === 'Enter') {
      e.preventDefault();
      onClickSearch();
    }
  };

  useEffect(() => {
    if (filterValue.length < 4) setSearchResult([]);
  }, [filterValue, JSON.stringify(searchResult)]);

  return (
    <form className={b()} onSubmit={handleFormSubmit} onKeyDown={handleKeyDown} tabIndex={0}>
      <div className={b('header')}>{locale.filter}</div>
      <div className={b('top')}>
        <DaysFilter filtersCount={2} activeDay={activeDay} setActiveDay={onChangeActiveDay} />
        <div className={b('or')}>{locale.or}</div>
        <div className={b('date')}>
          <div className={b('date-row')}>
            <div className={b('title')}>{locale.dateFrom}</div>
            <div className={b('date-input')}>
              <InputDate showTimeSelect selected={date.beginDate} onChange={onChangeBeginDate} />
            </div>
          </div>
          <div className={b('date-row')}>
            <div className={b('title')}>{locale.dateTo}</div>
            <div className={b('date-input')}>
              <InputDate showTimeSelect selected={date.endDate} onChange={onChangeEndDate} />
            </div>
          </div>
        </div>
      </div>
      <div className={b('bottom')}>
        <div className={b('inputs')}>
          <div className={b('input')}>
            <div className={b('title')}>{locale.user}</div>
            <SearchUserInput
              filterValue={filterValue}
              handleSearchChange={handleSearchChange}
              handleResultClick={handleResultClick}
              searchResult={searchResult}
              isLoadingSearchResult={isLoadingSearchResult}
              placeholder={locale.user}
              locale={locale}
            />
            <div className={b('advice')}>{` (${locale.enterTextInLowercaseOnly})`}</div>
          </div>
          <div className={b('input')}>
            <div className={b('title')}>{locale.provider}</div>
            <Select
              name="provider"
              items={providerItems}
              activeItem={filters.provider}
              onChange={handleProviderChange}
            />
          </div>
          <div className={b('input')}>
            <div className={b('title')}>{locale.statusBet}</div>
            <Select
              name="statusBets"
              items={statusBets}
              activeItem={filters.statusBet}
              onChange={handleStatusBetChange}
            />
          </div>
        </div>
        <div className={b('button')}>
          <div className={b('title')} style={{ color: 'transparent' }}>
            transparent placeholder
          </div>
          <Button size="low" text={locale.acceptFilter} type="submit" />
        </div>
      </div>
    </form>
  );
};

export default FilterBlock;
